import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../hooks/useApi";
import DrawerShell, { DrawerFailed, DrawerLoading, InlineDetailShell } from "./DrawerShell";
import IdentityMap from "./IdentityMap";
import { formatUtcDateTime } from "../utils/dateTime";
import PostureWeaknesses from "./PostureWeaknesses";
import IdentityControls from "./IdentityControls";
import IdentityActivity from "./IdentityActivity";
import IdentityExposure from "./IdentityExposure";
import IdentityExposureCard from "./IdentityExposureCard";
import { moreFindingsLabel } from "./moreFindingsLabel";

/**
 * Identities tab on Security Insights — the Defender for Identity Phase 1 inventory.
 *
 * Inventory only: no risks, no scores, no findings column. The spec's Phase 2 adds the
 * finding links; a "Risk Findings" column that always read 0 today would be invented data.
 *
 * The Source column leads with SourceProvider (where Microsoft says the identity came from) and
 * names the table as the secondary line, never a bare "Defender for Identity": IdentityInfo is
 * populated by Entra ID as well as MDI sensors.
 */

export const PROVIDER_LABELS = {
  ActiveDirectory:      "Active Directory (MDI sensor)",
  AzureActiveDirectory: "Entra ID",
  Hybrid:               "Hybrid (AD + Entra ID)",
};

/**
 * Microsoft's SourceProvider when it gave one. Some tenants' IdentityInfo has no SourceProvider
 * column at all (seen live 2026-09-23), so the ids are used instead and the label SAYS it is
 * inferred: a CloudSid with no OnPremSid is a cloud-only Entra account, an OnPremSid with no
 * CloudSid is on-prem only, both is hybrid. The stored SourceProvider stays null — the raw truth
 * is never rewritten, only the display fills the gap.
 */
export const providerLabel = (identity) => {
  if (!identity) return "Unknown provider";
  const raw = identity.sourceProvider;
  if (raw) return PROVIDER_LABELS[raw] || raw;
  const cloud = !!identity.cloudSid || !!identity.accountObjectId;
  const onPrem = !!identity.onPremSid;
  if (cloud && onPrem) return "Hybrid (inferred from ids)";
  if (cloud) return "Entra ID (inferred from ids)";
  if (onPrem) return "Active Directory (inferred from SID)";
  return "Unknown provider";
};

export const SOURCE_TABLE_LABEL = "via Defender XDR IdentityInfo";

/** Phase 3: the sources that can have seen an identity, in run order, with their chip labels. */
export const IDENTITY_SOURCE_LABELS = {
  Entra: "Entra",
  DefenderXdrIdentityInfo: "Defender XDR",
  Okta: "Okta",
  Nhi: "NHI",
  KnowBe4: "KnowBe4",
  EntraAppConsents: "App consents",
};
const SOURCE_CHIP_TONES = {
  Entra: "bg-sky-50 text-sky-700 border-sky-200",
  DefenderXdrIdentityInfo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Okta: "bg-violet-50 text-violet-700 border-violet-200",
  Nhi: "bg-teal-50 text-teal-700 border-teal-200",
  KnowBe4: "bg-orange-50 text-orange-700 border-orange-200",
  EntraAppConsents: "bg-cyan-50 text-cyan-700 border-cyan-200",
};
export const sourceLabel = (s) => IDENTITY_SOURCE_LABELS[s] || s;
/** Secondary line under the provider: the source that created the row. XDR keeps its table name. */
export const sourceVia = (i) => i.source === "DefenderXdrIdentityInfo" || !i.source ? SOURCE_TABLE_LABEL : `via ${sourceLabel(i.source)}`;

/** "Seen by" chips: which sources reported this identity. Never empty — the creating source at least. */
export const SeenByChips = ({ seenBy, labels = null }) => (
  <div className="flex flex-wrap gap-1" data-testid="seen-by">
    {(seenBy && seenBy.length ? seenBy : []).map(s => (
      <span key={s} className={`px-1.5 py-0.5 rounded border ${SOURCE_CHIP_TONES[s] || "bg-gray-50 text-gray-600 border-gray-200"}`}>{labels ? (labels[s] || s) : sourceLabel(s)}</span>
    ))}
  </div>
);

const PRIVILEGE_KIND_LABELS = {
  DirectoryRole: "directory role",
  PimEligible: "PIM eligible",
  MdiTag: "MDI tag",
  OktaAdminRole: "Okta admin role",
};
/** "Global Administrator — Entra directory role": a privilege is always a fact with a source. */
export const privilegeLabel = (p) => `${p.name} — ${sourceLabel(p.source)} ${PRIVILEGE_KIND_LABELS[p.kind] || p.kind}`;

/**
 * What the "Risky scope" chip means for THIS app, named.
 *
 * It used to read "Grants a scope on the risky list (mail, files, directory write…)" — a category,
 * ending in a literal ellipsis that readers took for text the browser had cut off. The backend
 * already sends the subset of this grant's scopes that put it on the list, so the chip can say
 * which permission it is actually objecting to.
 */
const riskyScopeTitle = (app) => {
  const names = app.riskyScopeNames ?? [];
  return names.length > 0
    ? `Grants ${names.join(", ")} — permission to read or change mail, files, directory objects or role assignments as this account.`
    : "Grants a permission to read or change mail, files, directory objects or role assignments as this account.";
};

const PAGE_SIZE = 50;

const SORTS = [
  { key: "displayName",    label: "Name" },
  { key: "upn",            label: "UPN" },
  { key: "accountType",    label: "Type" },
  { key: "sourceProvider", label: "Source" },
  { key: "privileged",     label: "Privileged" },
  { key: "lastActivity",   label: "Last activity" },
  // "lastSeen" stays a server sort key (whitelisted) but is no longer a column: it is in the drawer,
  // and the row must fit the page without a horizontal scroll.
];

const parseList = (json) => {
  if (!json) return [];
  try { const v = JSON.parse(json); return Array.isArray(v) ? v : []; } catch { return []; }
};

export const identityDisplayName = (i) =>
  i.displayName || i.accountUpn || (i.accountDomain && i.accountName ? `${i.accountDomain}\\${i.accountName}` : i.accountName) || i.identityKey;

const Stat = ({ label, value, tone = "text-gray-900" }) => (
  <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-xl font-semibold tabular-nums ${tone}`}>{value}</div>
  </div>
);

/** Builds the deep link the risk side uses to land on one identity's drawer. */
export const identityDeepLink = (identityId) =>
  `/SecurityInsights?tab=assets&identityId=${encodeURIComponent(identityId)}`;

export default function IdentityInventoryPanel({ organizationId, initialIdentityId = null, initialSearch = null }) {
  const apiCall = useApi();
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);      // { items, total, page, pageSize, counts }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // initialSearch arrives from a control's Affected Scope: the reader clicked an account name on a
  // failing control and expects to land on that account, not on an unfiltered inventory of 600.
  const [search, setSearch] = useState(initialSearch || "");
  const [searchDraft, setSearchDraft] = useState(initialSearch || "");
  const [accountType, setAccountType] = useState("");
  const [provider, setProvider] = useState("");
  const [enabled, setEnabled] = useState("");         // "" | "true" | "false"
  const [privileged, setPrivileged] = useState("");   // "" | "true"
  const [seen, setSeen] = useState("");               // "" | "false"
  const [hasFindings, setHasFindings] = useState(""); // "" | "true"   (Phase 2)
  const [seenBy, setSeenBy] = useState("");           // "" | source name (Phase 3)
  const [sort, setSort] = useState("displayName");
  const [desc, setDesc] = useState(false);
  const [selected, setSelected] = useState(null);     // detail DTO
  // The row click has to change the screen immediately. Opening on the RESPONSE meant a click did
  // nothing visible for as long as the request took, so people clicked again, and again.
  const [opening, setOpening] = useState(null);       // id being fetched, or null
  const [openFailed, setOpenFailed] = useState(false);
  const openRequest = useRef(0);
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [tableWidth, setTableWidth] = useState(0);
  const syncScroll = (from, to) => { if (from.current && to.current && to.current.scrollLeft !== from.current.scrollLeft) to.current.scrollLeft = from.current.scrollLeft; };
  const [config, setConfig] = useState(null);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (accountType) p.set("accountType", accountType);
    if (provider) p.set("sourceProvider", provider);
    if (enabled) p.set("enabled", enabled);
    if (privileged) p.set("privileged", privileged);
    if (seen) p.set("seenInLastSync", seen);
    if (hasFindings) p.set("hasFindings", hasFindings);
    if (seenBy) p.set("seenBy", seenBy);
    p.set("sort", sort);
    if (desc) p.set("desc", "true");
    p.set("page", String(page));
    p.set("pageSize", String(PAGE_SIZE));
    return p.toString();
  }, [search, accountType, provider, enabled, privileged, seen, hasFindings, seenBy, sort, desc, page]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await apiCall("GET", `/defender-identity/identities?${query}`));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load the identity inventory.");
    } finally { setLoading(false); }
  }, [apiCall, query]);

  useEffect(() => { load(); }, [load, organizationId]);

  // Keep the top scrollbar the same width as the table so both scroll the same distance.
  //
  // The measurement must NOT be written from inside the observer's callback. `tableWidth` sizes the
  // spacer in the strip above, that strip is a sibling of the element being observed, and changing
  // it reflows the observed element — so the callback resized the thing that triggered it. The
  // browser calls that "ResizeObserver loop completed with undelivered notifications" and CRA turns
  // it into a full-screen error. It stayed latent until something resized the table repeatedly
  // (dragging the drawer edge), which is when the loop became continuous.
  //
  // Two guards: the write is deferred to the next frame, out of the observer's delivery cycle, and
  // an unchanged width writes nothing at all.
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;

    let frame = 0;
    const apply = () => setTableWidth((current) => (current === el.scrollWidth ? current : el.scrollWidth));

    apply();
    if (typeof ResizeObserver === "undefined") return undefined;

    const ro = new ResizeObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; apply(); });
    });
    ro.observe(el);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    apiCall("GET", "/defender-identity/config")
      .then(c => { if (!cancelled) setConfig(c); })
      .catch(() => { /* the header just omits the sync line */ });
    return () => { cancelled = true; };
  }, [apiCall, organizationId]);

  // Any filter change goes back to page 1; the page state is the only thing that moves pages.
  const resetPage = (setter) => (v) => { setter(v); setPage(1); };

  const openDetail = async (id) => {
    // Open the panel first, fetch second. A ticket per request so a slow first click cannot
    // overwrite a faster second one with the wrong account.
    const request = ++openRequest.current;
    setSelected(null);
    setOpenFailed(false);
    setOpening(id);
    try {
      const detail = await apiCall("GET", `/defender-identity/identities/${id}`);
      if (openRequest.current !== request) return;
      setSelected(detail);
      setOpening(null);
    } catch {
      if (openRequest.current !== request) return;
      // A silent failure looks exactly like a click that did not register, which is the other half
      // of why people click repeatedly. Say so, and keep the id so Try again knows what to retry.
      setOpenFailed(true);
    }
  };

  const closeDetail = () => { openRequest.current++; setSelected(null); setOpening(null); setOpenFailed(false); };

  // Arriving from a risk's "Affected identities": open that drawer once, on mount.
  useEffect(() => {
    if (!initialIdentityId) return;
    const id = parseInt(initialIdentityId, 10);
    if (Number.isFinite(id)) openDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIdentityId]);

  const toggleSort = (key) => {
    if (sort === key) setDesc(d => !d); else { setSort(key); setDesc(false); }
    setPage(1);
  };

  const items = data?.items || [];
  const openFindings = data?.openFindings || {};
  const total = data?.total || 0;
  const counts = data?.counts;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const lastSync = config?.lastSync;

  return (
    <div className="space-y-4" data-testid="identity-inventory">
      <IdentityExposureCard onOpenIdentity={openDetail} />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold text-gray-900">Identity inventory</div>
          <div className="text-xs text-gray-500">
            Accounts read from the Defender XDR <span className="font-mono">IdentityInfo</span> table —
            Defender for Identity sensors and Entra ID. Inventory only; no risks are created from these rows.
          </div>
        </div>
        <div className="text-xs text-gray-400">
          {config?.lastSyncAt ? `Last synced ${formatUtcDateTime(config.lastSyncAt)}` : config ? "Not synced yet" : ""}
        </div>
      </div>

      {config && !config.defenderIdentityEnabled && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2">
          The Defender for Identity inventory is switched off for this organisation. Enable it under
          Integration Settings → Connector Settings to start syncing identities.
        </div>
      )}
      {lastSync?.truncated && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-3 py-2">
          The last sync hit the Advanced Hunting row cap (100,000). This inventory is the first 100,000
          identities by key, not the whole directory, and nothing was marked as no longer reported.
        </div>
      )}
      {lastSync && !lastSync.success && lastSync.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
          Last sync failed: {lastSync.error} The inventory shows the previous successful pull.
        </div>
      )}

      {/* Count strip */}
      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <Stat label="Identities" value={counts.total} />
          <Stat label="Privileged" value={counts.privileged} tone="text-rose-700" />
          <Stat label="Guests" value={counts.guests ?? 0} tone="text-amber-700" />
          <Stat label="Non-human" value={counts.nonHuman ?? counts.serviceAccounts} />
          <Stat label="Disabled" value={counts.disabled} tone="text-gray-500" />
          <Stat label="Sensitive-tagged" value={counts.sensitive} tone="text-amber-700" />
          <Stat label="Not in last sync" value={counts.notSeenInLastSync} tone={counts.notSeenInLastSync > 0 ? "text-amber-700" : "text-gray-900"} />
          <Stat label="Sources" value={(data?.sources || []).length || 1} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-wrap items-center gap-2" aria-busy={loading}>
        <form onSubmit={e => { e.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }} className="flex items-center gap-2">
          <input value={searchDraft} onChange={e => setSearchDraft(e.target.value)} placeholder="Search name, UPN, domain…"
            aria-label="Search identities"
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 w-56 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
          <button type="submit" className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200">Search</button>
        </form>
        <select value={provider} onChange={e => resetPage(setProvider)(e.target.value)} aria-label="Source provider" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">All sources</option>
          {Object.entries(PROVIDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={seenBy} onChange={e => resetPage(setSeenBy)(e.target.value)} aria-label="Seen by" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">Seen by any source</option>
          {(data?.sources || []).map(s => <option key={s} value={s}>Seen by {sourceLabel(s)}</option>)}
        </select>
        <select value={accountType} onChange={e => resetPage(setAccountType)(e.target.value)} aria-label="Account type" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">All types</option>
          <option value="User">User</option>
          <option value="ServiceAccount">Service account</option>
          <option value="ServicePrincipal">Service principal</option>
        </select>
        <select value={enabled} onChange={e => resetPage(setEnabled)(e.target.value)} aria-label="Account status" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5">
          <option value="">Any status</option>
          <option value="true">Enabled</option>
          <option value="false">Disabled</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-gray-700">
          <input type="checkbox" checked={privileged === "true"} onChange={e => resetPage(setPrivileged)(e.target.checked ? "true" : "")} className="rounded accent-indigo-600" />
          Privileged only
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-700">
          <input type="checkbox" checked={seen === "false"} onChange={e => resetPage(setSeen)(e.target.checked ? "false" : "")} className="rounded accent-indigo-600" />
          Not in last sync
        </label>
        <label className="flex items-center gap-1.5 text-xs text-gray-700">
          <input type="checkbox" checked={hasFindings === "true"} onChange={e => resetPage(setHasFindings)(e.target.checked ? "true" : "")} className="rounded accent-indigo-600" />
          Has findings
        </label>
        <div className="ml-auto text-xs text-gray-500 tabular-nums flex items-center gap-1.5" role="status" aria-live="polite" data-testid="inventory-count">
          {loading && <span className="inline-block w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" aria-hidden="true" />}
          {loading ? "Loading identities…" : `${total.toLocaleString()} identit${total === 1 ? "y" : "ies"}`}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}

      {/* Table. A second horizontal scrollbar sits at the top, synced with the real one, so a wide
          table on a narrow screen can be scrolled without first travelling to the bottom of 50 rows. */}
      <div ref={topScrollRef} onScroll={() => syncScroll(topScrollRef, tableScrollRef)} className="overflow-x-auto overflow-y-hidden h-3 -mb-3 relative z-10" aria-hidden="true" data-testid="inventory-top-scroll">
        <div style={{ width: tableWidth, height: 1 }} />
      </div>
      <div ref={tableScrollRef} onScroll={() => syncScroll(tableScrollRef, topScrollRef)} className="bg-white border border-gray-200 rounded-xl overflow-x-auto relative">
        {loading && <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden" data-testid="inventory-loading"><div className="h-full w-1/3 bg-indigo-500" style={{ animation: "identity-loader 1s linear infinite" }} /></div>}
        <style>{`@keyframes identity-loader { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
        <table className={`min-w-full text-xs transition-opacity ${loading ? "opacity-50 pointer-events-none" : ""}`}>
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              {SORTS.map(s => (
                <th key={s.key} scope="col" className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                  <button type="button" onClick={() => toggleSort(s.key)} className="hover:text-gray-900 flex items-center gap-1">
                    {s.label}{sort === s.key && <span aria-hidden="true">{desc ? "↓" : "↑"}</span>}
                  </button>
                </th>
              ))}
              <th scope="col" className="text-left font-semibold px-3 py-2 whitespace-nowrap" title="Which connectors reported this identity">Seen by</th>
              <th scope="col" className="text-left font-semibold px-3 py-2">Status</th>
              <th scope="col" className="text-left font-semibold px-3 py-2 whitespace-nowrap" title="Open findings that name this identity, under your own risk visibility">Risk findings</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && items.length === 0 && [0, 1, 2, 3, 4].map(k => (
              <tr key={`skeleton-${k}`} aria-hidden="true">
                {Array.from({ length: SORTS.length + 3 }).map((_, c) => (
                  <td key={c} className="px-3 py-3"><div className="h-3 rounded bg-gray-100 animate-pulse" style={{ width: `${40 + ((k * 7 + c * 13) % 50)}%` }} /></td>
                ))}
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={SORTS.length + 3} className="px-3 py-8 text-center text-gray-500">
                {total === 0 && !search && !provider && !accountType && !enabled && !privileged && !seen && !hasFindings && !seenBy
                  ? "No identities yet. Run a sync from Integration Settings, or wait for the nightly sync."
                  : "No identities match these filters."}
              </td></tr>
            )}
            {items.map(i => (
              <tr key={i.id} onClick={() => openDetail(i.id)} className="hover:bg-indigo-50/40 cursor-pointer">
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{identityDisplayName(i)}</div>
                  {i.department && <div className="text-gray-400">{i.department}{i.jobTitle ? ` · ${i.jobTitle}` : ""}</div>}
                </td>
                <td className="px-3 py-2 text-gray-700 font-mono max-w-[240px] truncate" title={i.accountUpn || undefined}>{i.accountUpn || <span className="text-gray-300">—</span>}</td>
                <td className="px-3 py-2 text-gray-700">
                  {i.accountType || <span className="text-gray-300">—</span>}
                  {i.userType === "Guest" && <span className="ml-1 px-1 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Guest</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="text-gray-800">{providerLabel(i)}</div>
                  <div className="text-gray-400">{sourceVia(i)}</div>
                </td>
                <td className="px-3 py-2">
                  {i.isPrivileged
                    ? <span className="inline-block whitespace-nowrap px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200" title={(i.privileges || []).map(privilegeLabel).join("\n")}>
                        Privileged{i.privileges && i.privileges.length > 0 ? ` · ${i.privileges.length}` : ""}
                      </span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                  {i.lastSignInAt
                    ? <span title="Directory sign-in (Entra / Okta)">{formatUtcDateTime(i.lastSignInAt)}</span>
                    : i.lastActivityAt ? formatUtcDateTime(i.lastActivityAt) : <span className="text-gray-300">not observed</span>}
                </td>
                <td className="px-3 py-2"><SeenByChips seenBy={i.seenBy} /></td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {i.isEnabled === true && <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">Enabled</span>}
                    {i.isEnabled === false && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200">Disabled</span>}
                    {i.isEnabled == null && <span className="px-1.5 py-0.5 rounded bg-gray-50 text-gray-400 border border-gray-200">Status unknown</span>}
                    {!i.seenInLastSync && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Not in last sync</span>}
                  </div>
                </td>
                <td className="px-3 py-2 tabular-nums">
                  {(openFindings[i.id] || 0) > 0
                    ? <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 font-semibold">{openFindings[i.id]}</span>
                    : <span className="text-gray-300">0</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pageCount > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 text-xs text-gray-600">
            <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Previous</button>
            <span>Page {page} of {pageCount}</span>
            <button type="button" disabled={page >= pageCount} onClick={() => setPage(p => p + 1)} className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {selected && <IdentityDetailDrawer detail={selected} onClose={closeDetail} organizationId={organizationId} />}
      {!selected && (opening !== null || openFailed) && (
        <DrawerShell onClose={closeDetail} label="Identity details"
          title={openFailed ? "Could not open this account" : "Opening…"}>
          {openFailed
            ? <DrawerFailed onRetry={() => openDetail(opening)} testId="identity-detail-failed"
                message="The account details could not be loaded." />
            : <DrawerLoading testId="identity-detail-loading" message="Loading account details…" />}
        </DrawerShell>
      )}
    </div>
  );
}

export const MATCH_KIND_LABELS = {
  ObjectId:    "matched by object id",
  Sid:         "matched by SID",
  Upn:         "matched by UPN",
  AccountName: "matched by account name",
  Email:       "matched by email",
  None:        "not in inventory",
};


/**
 * One account's chain, above the drawer's evidence sections. Collapsible because the drawer is a
 * narrow panel: the graph is the fastest read when you want it and dead space when you do not.
 */
function IdentityPath({ id, organizationId }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 rounded-lg border border-gray-200">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between px-2.5 py-2 text-left"
        data-testid="identity-path-toggle" aria-expanded={open}>
        <span className="text-[11px] uppercase tracking-wide text-gray-400">
          Path — identity → privilege → device → app → data
        </span>
        <span className="text-xs text-gray-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="border-t border-gray-100 p-2">
          {/* compact: the drawer is narrow, so the map's detail stacks under the canvas instead of
              taking 16rem beside it. Widen the drawer by dragging its left edge for more room. */}
          <IdentityMap organizationId={organizationId} identityId={id} height={360} compact />
        </div>
      )}
    </div>
  );
}


/**
 * What this account IS in the AI graph — the agents, applications and workloads whose node resolved
 * to this identity. Equivalence, not ownership: something a person merely owns is not listed here,
 * because "owns" is a relationship and would make an owner's agents read as workloads they run.
 *
 * Absent rather than empty when nothing resolves: an account that operates nothing should not grow
 * a heading telling you so.
 */
function IdentityRuns({ id }) {
  const apiCall = useApi();
  const [runs, setRuns] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiCall("GET", `/defender-identity/identities/${id}/runs`)
      .then(r => { if (!cancelled) setRuns(Array.isArray(r) ? r : []); })
      .catch(() => { if (!cancelled) setRuns([]); });
    return () => { cancelled = true; };
  }, [apiCall, id]);

  if (!runs?.length) return null;

  return (
    <>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Runs</div>
      <ul className="space-y-1" data-testid="identity-runs">
        {runs.map(r => (
          <li key={r.nodeId} className="text-xs rounded border border-gray-200 px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-gray-900 truncate" title={r.name}>{r.name}</span>
              <span className="text-[11px] text-gray-500 flex-shrink-0">{r.nodeKind}</span>
            </div>
            {/* The join evidence, per the spec's {key, matched_value, source, seen_at} contract —
                so an attribution can be audited rather than taken on trust. */}
            <div className={`text-[11px] mt-0.5 ${r.stale ? "text-amber-700" : "text-gray-500"}`}>
              {r.confidenceLabel}
              {r.matchValue && <> · matched on {r.matchKind} <span className="font-mono">{r.matchValue}</span></>}
              {r.observedAt && <> · identity seen {formatUtcDateTime(r.observedAt)}</>}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Every captured field, the associated findings (Phase 2), plus the raw source row for audit.
 * `inline` lays it out in the page (InlineDetailShell) rather than as a drawer. `onOpenRisk(riskId)`
 * and `onOpenDevice(deviceId)`, when given, keep a finding or a device on the calling page instead of
 * linking to the Risk Register / Security Insights (Security Operations uses both). `findingsLimit`
 * lists only that many findings, the rest behind "+ N more" (null lists them all).
 */
export function IdentityDetailDrawer({ detail, onClose, organizationId = null, inline = false, onOpenRisk = null, onOpenDevice = null, findingsLimit = null }) {
  const Shell = inline ? InlineDetailShell : DrawerShell;
  const i = detail.identity;
  const apiCall = useApi();
  const [findings, setFindings] = useState(null);   // null = loading / unavailable
  const [showAllFindings, setShowAllFindings] = useState(false);
  const shownFindings = findings && findingsLimit != null && !showAllFindings ? findings.slice(0, findingsLimit) : findings;
  const [devices, setDevices] = useState(null);     // Phase 5: where this account logged on (30 days)
  const [apps, setApps] = useState(null);           // Step 10: delegated app consents
  useEffect(() => {
    let cancelled = false;
    apiCall("GET", `/defender-identity/identities/${i.id}/findings`)
      .then(rows => { if (!cancelled) setFindings(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setFindings([]); });
    return () => { cancelled = true; };
  }, [apiCall, i.id]);

  useEffect(() => {
    let cancelled = false;
    apiCall("GET", `/defender-identity/identities/${i.id}/devices`)
      .then(rows => { if (!cancelled) setDevices(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setDevices([]); });
    return () => { cancelled = true; };
  }, [apiCall, i.id]);

  useEffect(() => {
    let cancelled = false;
    apiCall("GET", `/defender-identity/identities/${i.id}/apps`)
      .then(r => { if (!cancelled) setApps(r && Array.isArray(r.own) ? r : { own: [], tenantWide: [] }); })
      .catch(() => { if (!cancelled) setApps({ own: [], tenantWide: [] }); });
    return () => { cancelled = true; };
  }, [apiCall, i.id]);
  const tags = parseList(i.tagsJson);
  const roles = parseList(i.assignedRolesJson);
  const Row = ({ label, children }) => (
    <div className="grid grid-cols-3 gap-2 py-1.5 border-b border-gray-100 text-xs">
      <div className="text-gray-500">{label}</div>
      <div className="col-span-2 text-gray-800 break-all">{children ?? <span className="text-gray-300">—</span>}</div>
    </div>
  );
  return (
    <Shell title={identityDisplayName(i)} subtitle={providerLabel(i)} onClose={onClose}
      label="Identity details" testId="identity-detail">
        <div className="-mt-2 mb-2 text-xs"><SeenByChips seenBy={i.seenBy} /></div>

        {/* The path first, then the evidence. The sections below each state a true fact and leave
            the reader to notice that a directory role and a Mail.Send grant on this account are ONE
            finding; the chain draws that join. It replaces the reading, not the evidence — nothing
            below has been removed. */}
        <IdentityPath id={i.id} organizationId={organizationId} />

        <IdentityExposure id={i.id} />

        <IdentityControls id={i.id} />

        <IdentityRuns id={i.id} />

        <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-3 mb-1">Associated findings</div>
        {findings === null && <div className="text-xs text-gray-400 py-1.5">Loading…</div>}
        {findings && findings.length === 0 && <div className="text-xs text-gray-400 py-1.5">No findings name this identity.</div>}
        {findings && findings.length > 0 && (
          <ul className="space-y-1.5 py-1" data-testid="identity-findings">
            {shownFindings.map(f => (
              <li key={f.linkId} className={`text-xs rounded border px-2.5 py-2 ${f.isActive ? (f.isOpen ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-200") : "border-dashed border-gray-200 text-gray-400"}`}>
                <div className="flex items-center justify-between gap-2">
                  {onOpenRisk
                    ? <button type="button" onClick={() => onOpenRisk(f.unifiedRiskId)} className="font-medium text-gray-900 hover:underline truncate text-left" title={f.title || f.findingKey}>{f.title || f.findingKey}</button>
                    : <a href={`/risks?riskId=${encodeURIComponent(f.unifiedRiskId)}`} className="font-medium text-gray-900 hover:underline truncate" title={f.title || f.findingKey}>{f.title || f.findingKey}</a>}
                  <span className="flex gap-1 flex-shrink-0">
                    {f.severity && <span className="px-1 rounded bg-white border border-gray-200 text-gray-700">{f.severity}</span>}
                    <span className={`px-1 rounded border ${f.isOpen ? "bg-red-100 text-red-700 border-red-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>{f.isOpen ? "Open" : f.status}</span>
                  </span>
                </div>
                <div className="text-gray-500 mt-0.5">
                  {f.riskSource} · {MATCH_KIND_LABELS[f.matchKind] || f.matchKind}
                  {" · last seen "}{formatUtcDateTime(f.lastSeenAt)}
                  {!f.isActive && " · no longer named by this finding"}
                </div>
                {/* Two incidents can share a title; the finding key tells them apart. */}
                <div className="text-[10px] text-gray-400 font-mono truncate" title={f.findingKey}>{f.findingKey}</div>
              </li>
            ))}
          </ul>
        )}

        {findingsLimit != null && findings && findings.length > findingsLimit && (
          <button type="button" onClick={() => setShowAllFindings(x => !x)} aria-expanded={showAllFindings}
            data-testid="identity-findings-more"
            className="mt-1 text-xs font-medium text-indigo-700 hover:underline">
            {showAllFindings ? "Show fewer" : moreFindingsLabel(findings, shownFindings)}
          </button>
        )}

        <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Devices (logons, 30 days)</div>
        {devices === null && <div className="text-xs text-gray-400 py-1.5">Loading…</div>}
        {devices && devices.length === 0 && <div className="text-xs text-gray-400 py-1.5">No device logons observed for this account.</div>}
        {devices && devices.length > 0 && (
          <ul className="space-y-1 py-1" data-testid="identity-devices">
            {devices.map(d => (
              <li key={d.deviceAssetId} className="text-xs flex items-center justify-between gap-2 py-1 border-b border-gray-100">
                <span className="truncate">
                  {onOpenDevice
                    ? <button type="button" onClick={() => onOpenDevice(d.deviceAssetId)} className="font-medium text-gray-900 hover:underline text-left" title={d.displayName || d.dnsName}>{d.displayName || d.dnsName}</button>
                    : <a href={`/SecurityInsights?tab=assets&type=device&deviceId=${encodeURIComponent(d.deviceAssetId)}`} className="font-medium text-gray-900 hover:underline" title={d.displayName || d.dnsName}>{d.displayName || d.dnsName}</a>}
                  {d.isDomainController && <span className="ml-1 px-1 rounded bg-rose-50 text-rose-700 border border-rose-200">Domain controller</span>}
                  {d.riskScore && d.riskScore !== "None" && <span className="ml-1 px-1 rounded bg-orange-50 text-orange-700 border border-orange-200">{d.riskScore} risk</span>}
                </span>
                <span className="text-gray-500 whitespace-nowrap">{d.logons} logon{d.logons === 1 ? "" : "s"}{d.lastLogonAt ? ` · ${formatUtcDateTime(d.lastLogonAt)}` : ""}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Applications (consented)</div>
        {apps === null && <div className="text-xs text-gray-400 py-1.5">Loading…</div>}
        {apps && apps.own.length === 0 && <div className="text-xs text-gray-400 py-1.5">This account has consented to no application of its own.</div>}
        {apps && apps.own.length > 0 && (
          <ul className="space-y-1 py-1" data-testid="identity-apps">
            {apps.own.map(a => (
              <li key={a.servicePrincipalId} className="text-xs py-1 border-b border-gray-100">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900 truncate" title={a.displayName}>{a.displayName}</span>
                  <span className="flex gap-1 flex-shrink-0">
                    {a.isRisky && <span className="px-1 rounded bg-red-50 text-red-700 border border-red-200" title={riskyScopeTitle(a)}>Risky scope</span>}
                    {a.isPersistent && <span className="px-1 rounded bg-amber-50 text-amber-700 border border-amber-200" title="Granted offline_access: the app holds a refresh token, so its access survives a password change and a sign-out.">Persistent</span>}
                    {a.verifiedPublisher
                      ? <span className="px-1 rounded bg-green-50 text-green-700 border border-green-200" title="Microsoft has verified who publishes this app.">Verified publisher</span>
                      : <span className="px-1 rounded bg-gray-100 text-gray-500 border border-gray-200" title="Microsoft has not verified who publishes this app, so there is no attested identity behind it.">Unverified</span>}
                  </span>
                </div>
                <div className="text-gray-500 mt-0.5 break-all">{a.publisher ? `${a.publisher} · ` : ""}{a.scopes.join(", ")}</div>
              </li>
            ))}
          </ul>
        )}
        {apps && apps.tenantWideRead === false && (
          <div className="text-[11px] text-gray-500 py-1" data-testid="identity-apps-tenant-wide-not-read">
            Apps consented tenant-wide by an admin: not known — the last sync could not read them.
          </div>
        )}
        {apps && apps.tenantWide.length > 0 && (
          <details className="mt-1">
            <summary className="text-xs text-gray-500 cursor-pointer">{apps.tenantWide.length}{apps.tenantWideTruncated ? "+" : ""} app{apps.tenantWide.length === 1 ? "" : "s"} consented tenant-wide by an admin (apply to every account){apps.tenantWideTruncated ? " — partial list, the read hit its limit" : ""}</summary>
            <ul className="space-y-1 py-1 pl-2" data-testid="identity-apps-tenant-wide">
              {apps.tenantWide.map(a => (
                <li key={a.servicePrincipalId} className="text-xs text-gray-700">
                  <span className="font-medium">{a.displayName}</span>{a.isRisky && <span className="ml-1 px-1 rounded bg-red-50 text-red-700 border border-red-200" title={riskyScopeTitle(a)}>Risky scope</span>}
                  <span className="text-gray-400"> · {a.scopes.join(", ")}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
        {apps && !apps.ownGrantsInLastSync && apps.own.length > 0 && <div className="text-[11px] text-amber-700">These grants were not reported by the last complete sync.</div>}

        <IdentityActivity id={i.id} />

        <PostureWeaknesses entity="identities" id={i.id} label="identity" />

        <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Identity</div>
        <Row label="UPN">{i.accountUpn}</Row>
        <Row label="Account">{i.accountDomain && i.accountName ? `${i.accountDomain}\\${i.accountName}` : i.accountName}</Row>
        <Row label="Email">{detail.emailAddress}</Row>
        <Row label="Type">{i.accountType}{i.userType ? ` · ${i.userType}` : ""}</Row>
        <Row label="Status">{i.isEnabled === true ? "Enabled" : i.isEnabled === false ? "Disabled" : "Unknown"}</Row>
        <Row label="Account created">{i.accountCreatedAt ? formatUtcDateTime(i.accountCreatedAt) : null}</Row>
        <Row label="Department">{i.department}</Row>
        <Row label="Job title">{i.jobTitle}</Row>
        <Row label="Distinguished name">{detail.distinguishedName}</Row>

        <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Privileges</div>
        <Row label="Privileged">{i.isPrivileged ? "Yes" : "No"}</Row>
        <Row label="Privilege facts">
          {i.privileges && i.privileges.length
            ? <ul className="space-y-0.5" data-testid="privilege-facts">{i.privileges.map((p, k) => <li key={k}>{privilegeLabel(p)}</li>)}</ul>
            : null}
        </Row>
        <Row label="Roles">{roles.length ? roles.join(", ") : null}</Row>
        <Row label="Tags">{tags.length ? tags.join(", ") : null}</Row>
        <Row label="Criticality">{i.criticalityLevel}</Row>

        <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Microsoft identifiers</div>
        <Row label="Entra object id">{i.accountObjectId}</Row>
        <Row label="On-prem SID">{i.onPremSid}</Row>
        <Row label="Cloud SID">{i.cloudSid}</Row>
        <Row label="Report id">{detail.reportId}</Row>
        <Row label="Axaris key">{i.identityKey}</Row>

        <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Timeline</div>
        <Row label="Last sign-in">{i.lastSignInAt ? formatUtcDateTime(i.lastSignInAt) : "not reported — sign-in activity needs AuditLog.Read.All (Entra) or an Okta source"}</Row>
        <Row label="Last activity">{i.lastActivityAt ? formatUtcDateTime(i.lastActivityAt) : "not observed in the last 30 days"}</Row>
        <Row label="Source observed">{i.sourceObservedAt ? formatUtcDateTime(i.sourceObservedAt) : null}</Row>
        <Row label="First seen by Axaris">{formatUtcDateTime(i.firstSeenAt)}</Row>
        <Row label="Last seen by Axaris">{formatUtcDateTime(i.lastSeenAt)}</Row>
        <Row label="In last sync">{i.seenInLastSync ? "Yes" : "No — not reported by the last complete sync"}</Row>

        {detail.sources && detail.sources.length > 0 && (
          <>
            <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Seen by</div>
            <ul className="space-y-1.5" data-testid="source-records">
              {detail.sources.map(r => (
                <li key={`${r.source}-${r.sourceId}`} className="text-xs rounded border border-gray-200 px-2.5 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <SeenByChips seenBy={[r.source]} />
                    <span className={r.seenInLastSync ? "text-green-700" : "text-amber-700"}>{r.seenInLastSync ? "in last sync" : "not in last sync"}</span>
                  </div>
                  <div className="text-gray-500 mt-0.5 font-mono break-all">{r.sourceId}</div>
                  <div className="text-gray-400">first {formatUtcDateTime(r.firstSeenAt)} · last {formatUtcDateTime(r.lastSeenAt)}</div>
                  {r.attributesJson && (
                    <details className="mt-1">
                      <summary className="text-gray-500 cursor-pointer">What {sourceLabel(r.source)} knows</summary>
                      <pre className="mt-1 text-[10px] bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                        {(() => { try { return JSON.stringify(JSON.parse(r.attributesJson), null, 2); } catch { return r.attributesJson; } })()}
                      </pre>
                    </details>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {detail.rawJson && (
          <details className="mt-4">
            <summary className="text-xs text-gray-500 cursor-pointer">Raw source row</summary>
            <pre className="mt-2 text-[10px] bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
              {(() => { try { return JSON.stringify(JSON.parse(detail.rawJson), null, 2); } catch { return detail.rawJson; } })()}
            </pre>
          </details>
        )}
    </Shell>
  );
}
